import React, { useState, useRef, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, Target, BookOpen, Sparkles } from "lucide-react";
import { ThemeContext } from "../ThemeContext";

// Glassmorphism Card with Animations
const Card = ({ children, className = '', delay = 0 }) => {
  const [isHovered, setIsHovered] = useState(false);
  const cardRef = useRef(null);
  const { isDarkMode } = useContext(ThemeContext);
  
  // Hover colors: Light mode: #06bae4, Dark mode: #ff9e42
  const hoverColor = isDarkMode ? '#ff9e42' : '#06bae4';
  
  // Convert hex to rgba for shadows
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.6,
        delay: delay,
        type: "spring",
        stiffness: 100,
      }}
      whileHover={{
        scale: 1.02,
        y: -5,
        transition: { duration: 0.2 },
      }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className={`relative backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-3xl shadow-2xl p-6 border transition-all duration-300 ${className}`}
      style={{
        boxShadow: isHovered
          ? `0 10px 40px rgba(0, 0, 0, 0.1), 0 0 0 2px ${hexToRgba(hoverColor, 0.3)}, 0 0 20px ${hexToRgba(hoverColor, 0.5)}, 0 0 40px ${hexToRgba(hoverColor, 0.3)}`
          : '0 10px 40px rgba(0, 0, 0, 0.1)',
        borderColor: isDarkMode ? 'rgba(55, 65, 81, 0.5)' : 'rgba(255, 255, 255, 0.2)',
      }}
    >
      {children}
    </motion.div>
  );
};

const TutorialPage = () => {
    const { isDarkMode } = useContext(ThemeContext);
    const navigate = useNavigate();

    const handleStartGame = () => {
        navigate('/choose-topics');
    };

    return (
        <div className="min-h-screen flex flex-col relative pb-24 pl-0 md:pl-24 overflow-hidden bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
            {/* Main Content */}
            <div className="relative z-20 px-4 sm:px-6 lg:px-8 py-6">
                {/* Animated Header */}
                <motion.div
                    initial={{ opacity: 0, y: -50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, type: "spring" }}
                    className="text-center mb-8"
                >
                    <h1
                        key={`title-${isDarkMode}`}
                        className="text-5xl sm:text-6xl lg:text-7xl font-bold font-zonaProUltra mb-4 relative inline-block"
                        style={{
                            color: isDarkMode ? '#ff7a00' : '#212D82',
                            background: `linear-gradient(90deg, ${isDarkMode ? '#ff7a00' : '#212D82'} 0%, ${isDarkMode ? '#ff7a00' : '#212D82'} 40%, rgba(255, 122, 0, 0.8) 50%, ${isDarkMode ? '#ff7a00' : '#212D82'} 60%, ${isDarkMode ? '#ff7a00' : '#212D82'} 100%)`,
                            backgroundSize: '200% 100%',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            animation: 'shine 10s linear infinite',
                            lineHeight: '1.2',
                            paddingBottom: '0.25rem'
                        }}
                    >
                        Welcome to Solo Mode!
                    </h1>
                    <motion.p
                        className="text-gray-600 dark:text-gray-200 text-xl font-zonaProRegular"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3 }}
                    >
                        Sharpen your skills and take on challenges solo
                    </motion.p>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8 max-w-7xl mx-auto">
                {/* Left Column: Welcome Message */}
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2, duration: 0.6 }}
                        className="flex flex-col items-center justify-center lg:col-span-2"
                    >
                        <Card delay={0.3} className="w-full">
                            <div className="text-center flex flex-col items-center">
                                <motion.div
                                    animate={{ rotate: [0, 10, -10, 0] }}
                                    transition={{ duration: 2, repeat: Infinity }}
                                    className="mb-6"
                                >
                                    <Play className="w-20 h-20 text-cyan-500" />
                                </motion.div>
                                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold font-zonaProUltra text-gray-800 dark:text-white mb-4">
                                    Ready to Begin?
                                </h2>
                                <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-200 mb-8 max-w-xl font-zonaProRegular">
                                    Every question is a chance to level up, earn points, and climb the leaderboard — all at your own pace. Let the game begin!
                                </p>
                                <motion.button
                                    onClick={handleStartGame}
                                    className="py-3 px-8 bg-[#34c759] text-white text-xl sm:text-2xl md:text-2xl font-zonaProUltra rounded-full hover:shadow-lg hover:shadow-[#34c759]/50 transition-all"
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                        Start Game
                                </motion.button>
                </div>
                        </Card>
                    </motion.div>

                {/* Right Column: Game Rules */}
                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.4, duration: 0.6 }}
                        className="flex flex-col justify-center lg:col-span-3"
                    >
                        <Card delay={0.5} className="w-full">
                            <div className="flex items-center gap-3 mb-6">
                                <motion.div
                                    animate={{ rotate: 360 }}
                                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                                    className="p-2 rounded-xl bg-[#a6a6a6] text-white"
                                >
                                    <BookOpen size={24} />
                                </motion.div>
                                <h2
                                    key={`rules-${isDarkMode}`}
                                    className="text-2xl sm:text-3xl md:text-4xl font-bold font-zonaProUltra relative inline-block"
                                    style={{
                                        color: isDarkMode ? '#ff7a00' : '#212D82',
                                        background: `linear-gradient(90deg, ${isDarkMode ? '#ff7a00' : '#212D82'} 0%, ${isDarkMode ? '#ff7a00' : '#212D82'} 40%, rgba(255, 122, 0, 0.8) 50%, ${isDarkMode ? '#ff7a00' : '#212D82'} 60%, ${isDarkMode ? '#ff7a00' : '#212D82'} 100%)`,
                                        backgroundSize: '200% 100%',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        backgroundClip: 'text',
                                        animation: 'shine 10s linear infinite',
                                        lineHeight: '1.2',
                                        paddingBottom: '0.25rem'
                                    }}
                                >
                                    Game Rules
                                </h2>
                            </div>
                            <ul className="space-y-6 text-base sm:text-lg md:text-xl text-gray-700 dark:text-gray-100 font-zonaProRegular">
                                <motion.li
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.6 }}
                                    className="flex items-start"
                                >
                                    <motion.span
                                        animate={{ scale: [1, 1.1, 1] }}
                                        transition={{ duration: 2, repeat: Infinity, delay: 0 }}
                                        className="text-black dark:text-white mr-3 text-xl sm:text-2xl md:text-2xl"
                                    >
                                        🎯
                                    </motion.span>
                                <div className="text-left">
                                        <strong className="text-black dark:text-white">Start a Session:</strong> Pick a subject and then choose your difficulty level! You'll begin with <strong className="text-gray-800 dark:text-white">0 points</strong> for the session - every correct answer adds up and boosts your <strong className="text-gray-800 dark:text-white">total score instantly</strong>.
                                    </div>
                                </motion.li>
                                <motion.li
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.7 }}
                                    className="flex items-start"
                                >
                                    <motion.span
                                        animate={{ scale: [1, 1.1, 1] }}
                                        transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                                        className="text-black dark:text-white mr-3 text-xl sm:text-2xl md:text-2xl"
                                    >
                                        🎯
                                    </motion.span>
                                <div className="text-left">
                                        <strong className="text-black dark:text-white">Difficulty-Based Scoring:</strong> Each question's difficulty determines how many points you can earn or lose:
                                        <ul className="ml-6 mt-2 space-y-2 list-disc list-inside">
                                            <li><strong className="text-gray-800 dark:text-white">Easy Questions:</strong> <span className="text-green-600 dark:text-green-400 font-semibold">+100 points</span> for correct, <span className="text-red-600 dark:text-red-400 font-semibold">-100 points</span> for incorrect</li>
                                            <li><strong className="text-gray-800 dark:text-white">Medium Questions:</strong> <span className="text-green-600 dark:text-green-400 font-semibold">+200 points</span> for correct, <span className="text-red-600 dark:text-red-400 font-semibold">-200 points</span> for incorrect</li>
                                            <li><strong className="text-gray-800 dark:text-white">Hard Questions:</strong> <span className="text-green-600 dark:text-green-400 font-semibold">+300 points</span> for correct, <span className="text-red-600 dark:text-red-400 font-semibold">-300 points</span> for incorrect</li>
                                            <li><strong className="text-gray-800 dark:text-white">Unanswered Questions:</strong> <span className="text-gray-600 dark:text-gray-200 font-semibold">0 points</span> - no change to your score</li>
                                    </ul>
                                        <p className="mt-2 text-gray-600 dark:text-gray-200">A popup will show the difficulty and points for each question. For Random difficulty, you'll see this every question. For Easy/Medium/Hard, you'll see it once at the start of your session.</p>
                                </div>
                                </motion.li>
                                <motion.li
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.8 }}
                                    className="flex items-start"
                                >
                                    <motion.span
                                        animate={{ scale: [1, 1.1, 1] }}
                                        transition={{ duration: 2, repeat: Infinity, delay: 1 }}
                                        className="text-black dark:text-white mr-3 text-xl sm:text-2xl md:text-2xl"
                                    >
                                        🎯
                                    </motion.span>
                                <div className="text-left">
                                        <strong className="text-black dark:text-white">End Session:</strong> Want to wrap it up? Hit <em className="text-gray-800 dark:text-white">'End Session'</em> anytime. Your <strong className="text-gray-800 dark:text-white">session score resets</strong>, but all points you've earned are already safe and sound in your total score.
                                </div>
                                </motion.li>
                        </ul>
                        </Card>
                    </motion.div>
                </div>

                {/* Back Link */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.0 }}
                    className="text-center mt-8"
                >
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Link
                to="/options"
                            className="inline-block font-medium text-black dark:text-white hover:text-gray-700 dark:hover:text-gray-100 font-zonaProRegular text-base sm:text-lg md:text-xl transition-colors"
            >
                            ← Back to Game Modes
            </Link>
                    </motion.div>
                </motion.div>
            </div>
        </div>
    );
};

export default TutorialPage;